import { useEffect, useState } from 'react';
import { Button, Checkbox, DatePicker, Input, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CommentOutlined,
  FlagOutlined,
  LikeOutlined,
  PictureOutlined,
  PushpinOutlined,
  StarFilled,
  VideoCameraOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import UserPicker from 'pages/star-shared/components/user-picker';
import { useFeed } from './shared/context';
import { AuthorCell, MediaStrip, StatusNote, StatusPill } from './shared/components';
import { FeedAdminPostRow } from './shared/types';
import { fmtDate, fmtTime, useFeedList, useUrlFilters } from './shared/utils';
import { ContentActionsMenu, contentActions, useContentActions } from './moderate-modal';
import styles from './styles.module.scss';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'PUBLISHED', label: 'Đang hiện' },
  { value: 'PENDING_REVIEW', label: 'Đang giữ' },
  { value: 'HIDDEN', label: 'Đã ẩn' },
  { value: 'REJECTED', label: 'Bị từ chối' },
  { value: 'FAILED', label: 'Video lỗi' },
  { value: 'DELETED', label: 'Đã xoá' },
];

const AUTHOR_TYPE_OPTIONS = [
  { value: 'USER', label: 'Người dùng' },
  { value: 'PAGE', label: 'Tên HP Media' },
];

const VERDICT_OPTIONS = [
  { value: 'REVIEW', label: 'Máy: Cần xem' },
  { value: 'BLOCK', label: 'Máy: Bị chặn' },
  { value: 'SKIPPED', label: 'Máy: Chưa soi' },
];

const HIDDEN_REASON_OPTIONS = [
  { value: 'REPORTS', label: 'Nhiều người báo cáo' },
  { value: 'AI_BLOCK', label: 'Máy chặn (AI)' },
  { value: 'MODERATOR', label: 'Quản lý ẩn' },
];

/** Dòng dưới nội dung: "3 ảnh" / "Video", "Đã ghim", "Quan trọng đến 05/10". */
export const PostBadges = ({ row }: { row: FeedAdminPostRow }) => {
  const bits = [];
  if (row.mediaCount > 0) {
    bits.push(
      <span key="media">
        {row.mediaKind === 'VIDEO' ? <VideoCameraOutlined /> : <PictureOutlined />}{' '}
        {row.mediaKind === 'VIDEO' ? 'Video' : `${row.mediaCount} ảnh`}
      </span>
    );
  }
  if (row.isPinned) {
    bits.push(
      <span key="pin">
        <PushpinOutlined /> Đã ghim
      </span>
    );
  }
  if (row.isImportant && row.importantUntil && dayjs(row.importantUntil).isAfter(dayjs())) {
    bits.push(
      <span key="imp" style={{ color: colors.starInk }}>
        <StarFilled style={{ color: colors.star }} /> Quan trọng đến {dayjs(row.importantUntil).format('DD/MM')}
      </span>
    );
  }
  if (!bits.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', fontSize: 12, color: colors.textSecondary }}>
      {bits}
    </div>
  );
};

/** Tab "Bài viết" (03 §6) — D3 + bộ lọc trên URL. */
const PostsTab = () => {
  const { refreshKey, openPost, myRank, access } = useFeed();
  const { get, set, clear } = useUrlFilters();
  const { run, modals } = useContentActions();
  const page = Number(get('page')) || 1;
  const authorId = get('authorId');
  const [search, setSearch] = useState(get('q') || '');

  const qParam = get('q') || '';
  useEffect(() => setSearch(qParam), [qParam]);

  const query = {
    status: get('status'),
    authorId,
    authorType: get('authorType'),
    q: get('q'),
    verdict: get('verdict'),
    flagged: get('flagged') ? true : undefined,
    hiddenReason: get('hiddenReason'),
    hasReports: get('hasReports') ? true : undefined,
    from: get('from'),
    to: get('to'),
    page,
    limit: PAGE_SIZE,
  };
  const { items, total, loading } = useFeedList<FeedAdminPostRow>(API.feedAdminPosts, query, { refreshKey });

  const from = get('from');
  const to = get('to');

  const columns: ColumnsType<FeedAdminPostRow> = [
    {
      title: 'Người đăng',
      key: 'author',
      width: 192,
      render: (_, row) => (
        <AuthorCell
          author={row.author}
          actedBy={row.actedBy}
          onClickName={
            row.author?.type === 'USER' && row.author.userId
              ? () => set({ authorId: row.author.userId, authorName: row.author.name })
              : undefined
          }
        />
      ),
    },
    {
      title: 'Nội dung',
      key: 'content',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 10, minWidth: 240 }}>
          <MediaStrip thumbs={row.thumbs} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 2 }}>
              {row.contentPreview || <span style={{ color: colors.textMuted }}>(không có chữ)</span>}
            </Typography.Paragraph>
            <PostBadges row={row} />
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 160,
      render: (_, row) => (
        <div>
          <StatusPill status={row.status} deletedAt={row.deletedAt} />
          <StatusNote
            hiddenReason={row.status === 'HIDDEN' ? row.hiddenReason : null}
            needsReview={row.needsReview && !row.deletedAt}
            keywordVerdict={row.keywordVerdict}
            textVerdict={row.textVerdict}
            mediaVerdicts={row.mediaVerdicts}
          />
        </div>
      ),
    },
    {
      title: 'Tương tác',
      key: 'stats',
      width: 104,
      render: (_, row) => (
        <div style={{ fontSize: 12, lineHeight: '20px', color: colors.textSecondary }}>
          <div>
            <LikeOutlined /> {row.reactionCount}
          </div>
          <div>
            <CommentOutlined /> {row.commentCount}
          </div>
          {row.openReportCount > 0 && (
            <div style={{ color: colors.dangerInk, fontWeight: 600 }}>
              <FlagOutlined /> {row.openReportCount}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Thời gian',
      key: 'time',
      width: 124,
      render: (_, row) => (
        <div style={{ fontSize: 13, lineHeight: '20px' }}>
          <div>{fmtDate(row.createdAt)}</div>
          <div style={{ color: colors.textSecondary }}>
            {fmtTime(row.createdAt)} · #{row.id}
          </div>
          {row.editedAt && <div style={{ fontSize: 12, color: colors.textSecondary }}>đã sửa</div>}
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 112,
      fixed: 'right',
      render: (_, row) => (
        <Space size={6}>
          <Button size="small" onClick={() => openPost(row.id)}>
            Xem
          </Button>
          <ContentActionsMenu
            size="small"
            items={contentActions('post', row, { myRank, canMarkImportant: access.canMarkImportant })}
            onRun={(key) => run('post', row.id, key)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className={styles.filters}>
        <Select
          allowClear
          placeholder="Tất cả (chưa xoá)"
          style={{ width: 180 }}
          value={get('status')}
          options={STATUS_OPTIONS}
          onChange={(v) => set({ status: v })}
        />
        <div style={{ width: 260 }}>
          <UserPicker
            size="middle"
            placeholder="Người đăng"
            value={authorId ? { value: Number(authorId), label: get('authorName') || `#${authorId}` } : undefined}
            onChange={(v) =>
              set({ authorId: v?.value, authorName: v?.label ? String(v.label).split(' — ')[0] : undefined })
            }
          />
        </div>
        <Select
          allowClear
          placeholder="Loại"
          style={{ width: 170 }}
          value={get('authorType')}
          options={AUTHOR_TYPE_OPTIONS}
          onChange={(v) => set({ authorType: v })}
        />
        <Input.Search
          allowClear
          placeholder="Tìm trong nội dung"
          style={{ width: 240 }}
          maxLength={100}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => set({ q: v.trim() })}
        />
        <Select
          allowClear
          placeholder="Máy gắn cờ"
          style={{ width: 190 }}
          value={get('verdict')}
          options={VERDICT_OPTIONS}
          onChange={(v) => set({ verdict: v })}
        />
        <Select
          allowClear
          placeholder="Lý do ẩn"
          style={{ width: 170 }}
          value={get('hiddenReason')}
          options={HIDDEN_REASON_OPTIONS}
          onChange={(v) => set({ hiddenReason: v })}
        />
        <Checkbox checked={!!get('flagged')} onChange={(e) => set({ flagged: e.target.checked })}>
          Cần xem
        </Checkbox>
        <Checkbox checked={!!get('hasReports')} onChange={(e) => set({ hasReports: e.target.checked })}>
          Có báo cáo
        </Checkbox>
        <DatePicker.RangePicker
          // styles/index.scss đặt .ant-picker-range rộng 100% cho cả admin → chiếm trọn một hàng lọc.
          style={{ width: 280 }}
          format="DD/MM/YYYY"
          placeholder={['Từ ngày', 'Đến ngày']}
          value={from && to ? [dayjs(from), dayjs(to)] : null}
          onChange={(range) =>
            set({
              from: range?.[0] ? range[0].format('YYYY-MM-DD') : undefined,
              to: range?.[1] ? range[1].format('YYYY-MM-DD') : undefined,
            })
          }
        />
        <Button onClick={() => clear(['tab', 'post'])}>Xoá lọc</Button>
      </div>

      <div className="custom-table">
        <Table<FeedAdminPostRow>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={items}
          locale={{ emptyText: 'Chưa có bài nào khớp bộ lọc.' }}
          scroll={items.length ? { x: 960 } : undefined}
          rowClassName={(row) => (row.deletedAt ? styles.deletedRow : '')}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (p) => set({ page: p }),
          }}
        />
      </div>
      {modals}
    </div>
  );
};

export default PostsTab;
